import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { DOCUMENT, NgOptimizedImage } from '@angular/common';
import { ImagePreviewService } from './image-preview.service';
import { CMSFileDetail } from 'www-cms';
import { DesktopImageSizeType, fadeInOut, optimizedImageSizes } from 'common';
import { SpinnerComponent } from 'ui-elements/spinner/spinner.component';
import { WWW_CONFIG, WWWConfig } from '../../models/config.model';
import { ButtonComponent } from 'ui-elements/button/button.component';

@Component({
  selector: 'ss-www-image-preview',
  imports: [NgOptimizedImage, SpinnerComponent, ButtonComponent],
  templateUrl: './image-preview.component.html',
  styles: [
    `
      :host {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: 500;
      }

      :host:not(.overflow-hidden) {
        pointer-events: none;
      }

      :host.overflow-hidden {
        pointer-events: auto;
      }
    `,
  ],
  host: {
    '[class]': '!!imageDetail() ? "overflow-hidden" : ""',
    '(click)': 'handleBackgroundClick($event)',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeInOut],
})
export class ImagePreviewComponent implements OnInit, OnDestroy {
  private wwwConfig = inject<WWWConfig>(WWW_CONFIG);
  private readonly document = inject(DOCUMENT);
  private readonly imagePreviewService = inject(ImagePreviewService);
  assetsBaseUrl = this.wwwConfig.baseAssetUrl;
  imageDetail: WritableSignal<Partial<CMSFileDetail> | null> =
    this.imagePreviewService.previewImage;
  downloadDetail: WritableSignal<Partial<CMSFileDetail> | null> =
    this.imagePreviewService.previewDownloadNonImageAsset;
  desktopImageSize: WritableSignal<DesktopImageSizeType> =
    this.imagePreviewService.desktopImageSize;
  isDownloadable: WritableSignal<boolean> =
    this.imagePreviewService.isDownloadable;
  optimizedImageSizes = optimizedImageSizes;

  backgroundChanges = effect(() => {
    const image = this.imageDetail();

    if (!!image) {
      this.document.body.style.overflow = 'hidden';
    } else {
      this.document.body.style.overflow = '';
    }
  });

  ngOnInit(): void {
    this.document.addEventListener('keydown', (event) =>
      this.handleEscapeKey(event)
    );
  }

  ngOnDestroy(): void {
    this.document.removeEventListener('keydown', (event) =>
      this.handleEscapeKey(event)
    );
  }

  imageLoaded = signal<boolean>(false);
  imageError = signal<boolean>(false);

  handleEscapeKey(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.closePreview();
    }
  }

  handleBackgroundClick(event: MouseEvent) {
    // Close if clicking on the background (not the image)
    if (event.target === event.currentTarget) {
      this.closePreview();
    }
  }

  closePreview() {
    this.imagePreviewService.previewImage.set(null);
    this.imagePreviewService.previewDownloadNonImageAsset.set(null);
    this.imagePreviewService.desktopImageSize.set('half');
    this.imagePreviewService.isDownloadable.set(false);
    this.imageLoaded.set(false);
    this.imageError.set(false);
  }
}
